import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/utils-helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { DollarSign, TrendingUp, Users, ShoppingBag } from 'lucide-react';

export default function AdminAnalytics() {
  const [revenueData, setRevenueData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [revRes, userRes, dashRes] = await Promise.all([
          api.get('/admin/analytics/revenue'),
          api.get('/admin/analytics/users').catch(() => ({ data: { daily_signups: [], daily: [] } })),
          api.get('/admin/dashboard')
        ]);
        // Handle both response formats: {daily: []} or {daily_revenue: []}
        setRevenueData(revRes.data.daily || revRes.data.daily_revenue || []);
        setUserData(userRes.data.daily_signups || userRes.data.daily || []);
        setDashboard(dashRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-64" />)}</div></div>;

  const kpis = [
    { label: 'Total Revenue', value: formatCurrency(dashboard?.total_revenue), icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Service Fees', value: formatCurrency(dashboard?.total_service_fees), icon: TrendingUp, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Orders', value: dashboard?.total_orders || 0, icon: ShoppingBag, color: 'text-amber-600 bg-amber-50' },
    { label: 'Total Users', value: dashboard?.total_users || 0, icon: Users, color: 'text-purple-600 bg-purple-50' },
  ];

  // Filter out zero-revenue days for cleaner chart
  const filteredRevenue = revenueData.filter(d => d.revenue > 0 || d.orders > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Revenue & Analytics</h1>
        <p className="text-muted-foreground mt-1">Track revenue trends, user growth, and platform performance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="kpi-card">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-muted-foreground">{kpi.label}</span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${kpi.color}`}><Icon className="h-4 w-4" /></div>
                </div>
                <p className="text-2xl font-semibold tabular-nums" data-font="display">{kpi.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Daily Revenue</CardTitle></CardHeader>
          <CardContent>
            {filteredRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={filteredRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 90%)" strokeOpacity={0.6} />
                  <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => v?.slice(5)} />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid hsl(214 20% 90%)', fontSize: '12px' }} formatter={v => [`$${Number(v).toFixed(2)}`, 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(198 84% 32%)" fill="hsl(198 84% 32%)" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-center py-12 text-muted-foreground">No revenue data available</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Daily Orders</CardTitle></CardHeader>
          <CardContent>
            {filteredRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 90%)" strokeOpacity={0.6} />
                  <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => v?.slice(5)} />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid hsl(214 20% 90%)', fontSize: '12px' }} />
                  <Bar dataKey="orders" fill="hsl(174 55% 36%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center py-12 text-muted-foreground">No order data available</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
